import UserProfileSkeleton from '@/components/skeletons/UserProfileSkeleton';
import UserProfile from '@/components/UserProfile';
import { useUserProfile } from '@/context/UserProfileContext';
import type { ProfileSummary } from '@/types/profile';

import { api } from '@/utils/api';
import { IonContent, IonPage } from '@ionic/react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

export default function UserPage() {
  const { username } = useParams<{ username: string }>();
  const { t } = useTranslation();
  const { profile } = useUserProfile(); // Get current user profile from context
  const [profileDetailed, setProfileDetailed] = useState<ProfileSummary | null>(null);
  const [userPosts, setUserPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data } = await api.get(`/profile/${username}/details`);
        setProfileDetailed(data as ProfileSummary);
      } catch (err: any) {
        console.error('Error fetching profile:', err);

        if (err.response?.status === 404) {
          setError('profileNotFound');
        } else {
          console.error('Error fetching profile details:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [username, profile]);

  const fetchUserPosts = useCallback(async (isInitial: boolean = false) => {
    if (isInitial) {
      setPostsLoading(true);
      setPage(1);
    }

    if (!profileDetailed?.profile?.id) return;

    try {
      const currentPage = isInitial ? 1 : page;
      // Use the main posts endpoint with a filter for the user's profile ID
      let apiUrl = `/posts?page=${currentPage}&limit=20`;

      // Add profileId filter to get only posts from this user
      apiUrl += `&profileId=${profileDetailed.profile.id}`;

      // Include current user's profileId for like status if available
      if (profile?.id) {
        apiUrl += `&viewerId=${profile.id}`;
      }

      const { data: { posts: newPosts, metadata } } = await api.get(apiUrl);
      const hasNewPosts = newPosts && newPosts.length > 0;

      if (isInitial) {
        setUserPosts(newPosts);
      } else if (hasNewPosts) {
        // Filter out any duplicates before adding new posts
        setUserPosts(prevPosts => {
          const existingPostIds = new Set(prevPosts.map((post: any) => post.id));
          const uniqueNewPosts = newPosts.filter((post: any) => !existingPostIds.has(post.id));

          if (uniqueNewPosts.length === 0) {
            // If all posts were duplicates, we've reached the end
            setHasMore(false);
            return prevPosts;
          }

          return [...prevPosts, ...uniqueNewPosts];
        });
      }

      // Check if we've reached the end of available posts
      const reachedEnd = !hasNewPosts || (newPosts.length < 20) || (currentPage >= metadata?.totalPages);
      setHasMore(!reachedEnd);

      if (!isInitial && hasNewPosts) {
        setPage(prevPage => prevPage + 1);
      }
    } catch (error) {
      console.error('Error fetching user posts:', error);
    } finally {
      setPostsLoading(false);
    }
  }, [page, profile?.id, profileDetailed?.profile?.id]);

  useEffect(() => {
    if (profileDetailed?.profile?.id) {
      fetchUserPosts(true);
    }
  }, [profileDetailed?.profile?.id, fetchUserPosts]);

  return (
    <IonPage>
      {loading ? (
        <UserProfileSkeleton />
      ) : profileDetailed ? (
        <UserProfile
          data={{ ...profileDetailed, posts: userPosts }}
          edit={false}
          profileDetailed={profileDetailed}
          postsLoading={postsLoading}
          loadMorePosts={() => fetchUserPosts(false)}
          hasMorePosts={hasMore}
        />
      ) : error === 'profileNotFound' ? (
        <IonContent>
          <div className="flex flex-col items-center justify-center h-full p-4 text-center">
            <div className="mb-6">
              <DotLottieReact className="w-full" src="/assets/lottie/notfound.json" autoplay loop />
            </div>
            <h2 className="text-xl font-medium mb-2">{t('profile_not_found')}</h2>
          </div>
        </IonContent>
      ) : (
        <div className="flex flex-col items-center justify-center h-full p-4 text-center">
          <h2 className="text-xl font-medium mb-2">{t('cant_load_profile')}</h2>
        </div>
      )}
    </IonPage>
  );
}